<template>
  <div class="min-h-screen bg-background">
   
    <!-- Loading State -->
    <div v-if="loading" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      <p class="mt-4 text-white">Chargement du produit...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="container mx-auto px-4 py-16 text-center">
      <div class="text-red-600 mb-4">
        <icon name="AlertCircle" class="w-16 h-16 mx-auto" />
      </div>
      <p class="text-lg text-muted-foreground mb-4">Produit non trouvé</p>
      <router-link to="/products">
        <button class="bg-primary hover:bg-primary-hover text-primary-foreground px-6 py-2 rounded-lg">
          Retour aux produits
        </button>
      </router-link>
    </div>

    <!-- Product Content -->
    <div v-else-if="product" class="container mx-auto px-4 py-8">
      <!-- Breadcrumb -->
      <nav class="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
        <router-link to="/" class="hover:text-secondary">Accueil</router-link>
        <span>/</span>
        <router-link :to="`/category/${categorySlug}`" class="hover:text-secondary">
          {{ product.category?.slug || 'Catégorie' }}
        </router-link>
        <span>/</span>
        <span class="text-foreground line-clamp-1">{{ product.name }}</span>
      </nav>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <!-- Product Images -->
        <div class="space-y-4">
          <!-- Main Image -->
          <div class="relative aspect-square overflow-hidden rounded-lg bg-muted">
            <img 
              :src="currentImage" 
              :alt="product.name"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <button
              v-if="hasMultipleImages"
              @click="prevImage"
              :disabled="!hasMultipleImages"
              class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-2 shadow-md transition-colors disabled:opacity-50"
            >
              <ChevronLeft class="h-5 w-5" />
            </button>
            <button
              v-if="hasMultipleImages"
              @click="nextImage"
              :disabled="!hasMultipleImages"
              class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-2 shadow-md transition-colors disabled:opacity-50"
            >
              <ChevronRight class="h-5 w-5" />
            </button>
            
            <!-- Discount Badge -->
            <span v-if="hasDiscount" class="absolute top-4 left-4 bg-destructive text-destructive-foreground px-3 py-1 rounded-sm text-sm font-semibold">
              -{{ discountPercentage }}%
            </span>
          </div>
          
          <!-- Thumbnails -->
          <div v-if="hasMultipleImages" class="flex space-x-2 overflow-x-auto pb-2">
            <button
              v-for="(image, index) in productImages"
              :key="index"
              @click="setCurrentImageIndex(index)"
              :class="[
                'flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors',
                currentImageIndex === index ? 'border-primary' : 'border-border'
              ]"
            >
              <img 
                :src="image" 
                :alt="product.name + ' ' + (index + 1)"
                itemprop="image"
                 
                class="w-full h-full object-cover"
                @error="handleImageError"
              />
            </button>
          </div>
        </div>

        <!-- Product Info -->
        <div class="space-y-6">
          <!-- Brand and Wishlist -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span v-if="product.brand?.name" class="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm">
                {{ product.brand.name }}
              </span>
              <span v-if="product.model?.name" class="bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm">
                {{ product.model.name }}
              </span>
            </div>
            <button
              @click="toggleWishlist"
              class="p-2 hover:bg-accent rounded-full transition-colors"
              :title="isInWishlist ? 'Retirer des favoris' : 'Ajouter aux favoris'"
            >
              <Heart :class="['h-5 w-5', isInWishlist ? 'fill-destructive text-destructive' : 'text-muted-foreground']" />
            </button>
          </div>
          
          <!-- Product Name -->
          <h1 class="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {{ product.name }}
          </h1>

          <!-- Rating -->
          <div class="flex items-center space-x-4 mb-4">
            <div class="flex items-center space-x-1">
              <Star
                v-for="i in 5"
                :key="i"
                :class="[
                  'h-4 w-4',
                  i <= Math.floor(product.rating_avg || 0)
                    ? 'fill-secondary text-secondary'
                    : 'text-muted-foreground/30'
                ]"
              />
              <span class="text-sm text-muted-foreground ml-2">
                {{ product.rating_avg?.toFixed(1) || '0.0' }} ({{ product.reviews_count || 0 }} avis)
              </span>
            </div>
          </div>

          <!-- Price -->
          <div class="flex items-center space-x-4 mb-6">
            <span class="text-2xl md:text-3xl font-bold text-foreground">
              {{ formatPrice(finalPrice) }}
            </span>
            <span v-if="hasDiscount" class="text-lg md:text-xl text-muted-foreground line-through">
              {{ formatPrice(product.price) }}
            </span>
          </div>

          <!-- Description -->
          <p class="text-muted-foreground mb-6">
            {{ product.description }}
          </p>

          <!-- Size Selection -->
          <div v-if="hasSizes">
            <h3 class="font-semibold mb-3">Taille</h3>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="size in product.sizes"
                :key="size"
                @click="selectSize(size)"
                :class="[
                  'px-4 py-2 border rounded-md text-sm font-medium transition-colors',
                  selectedSize === size
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:border-primary'
                ]"
              >
                {{ size }}
              </button>
            </div>
          </div>

          <!-- Color Selection -->
          <div v-if="hasColors">
            <h3 class="font-semibold mb-3">Couleur</h3>
            <div class="flex flex-wrap gap-3">
              <button
                v-for="color in product.colors"
                :key="color"
                @click="selectColor(color)"
                :class="[
                  'w-10 h-10 rounded-full border-2 transition-all',
                  selectedColor === color
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-border'
                ]"
                :style="{ backgroundColor: color }"
                :title="color"
              ></button>
            </div>
          </div>

          <!-- Specifications -->
          <div v-if="hasSpecifications" class="bg-card rounded-lg border border-border p-4">
            <h4 class="font-semibold mb-3">Spécifications</h4>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="(value, key) in product.specifications" :key="key" class="text-sm">
                <span class="text-muted-foreground">{{ key }}:</span>
                <span class="ml-2 text-foreground">{{ value }}</span>
              </div>
            </div>
          </div>

          <!-- Quantity -->
          <div>
            <h3 class="font-semibold mb-3">Quantité</h3>
            <div class="flex items-center space-x-3">
              <button
                @click="decreaseQuantity"
                :disabled="quantity <= 1"
                class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 w-9 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Minus class="h-4 w-4" />
              </button>
              <span class="text-lg font-medium w-12 text-center">
                {{ quantity }}
              </span>
              <button
                @click="increaseQuantity"
                :disabled="quantity >= product.stock_quantity"
                class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 w-9 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus class="h-4 w-4" />
              </button>
              <span class="text-sm text-muted-foreground">
                ({{ product.stock_quantity }} disponibles)
              </span>
            </div>
          </div>

          <!-- Add to Cart -->
          <div class="space-y-3">
            <button 
              @click="addToCart"
              :disabled="!product.in_stock || product.stock_quantity <= 0"
              :class="[
                'w-full h-12 px-4 rounded-lg font-medium flex items-center justify-center transition-normal',
                product.in_stock && product.stock_quantity > 0
                  ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                  : 'bg-gray-400 cursor-not-allowed text-gray-700'
              ]"
            >
              <ShoppingCart class="h-5 w-5 mr-2" />
              {{ product.stock_quantity > 0 ? 'Ajouter au panier' : 'Rupture de stock' }}
            </button>
            
            <p v-if="product.in_stock && product.stock_quantity > 0" class="text-sm text-green-600 flex items-center">
              <Check class="h-4 w-4 mr-1" />
              En stock - Livraison gratuite à partir de 50 000 FCFA
            </p>
            <p v-else class="text-sm text-red-600 flex items-center">
              <icon name="AlertCircle" class="h-4 w-4 mr-1" />
              Ce produit est actuellement indisponible
            </p>
          </div>

          <!-- Delivery Info -->
          <div class="grid grid-cols-2 gap-4 pt-4 border-t border-border">
            <div class="flex items-center gap-2 text-sm">
              <Truck class="h-5 w-5 text-secondary" />
              <span>Livraison 24-48h</span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <Shield class="h-5 w-5 text-secondary" />
              <span>Paiement sécurisé</span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <RefreshCw class="h-5 w-5 text-secondary" />
              <span>Retour 14 jours</span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <Package class="h-5 w-5 text-secondary" />
              <span>Produit authentique</span>
            </div>
          </div>

          <!-- Features -->
          <div v-if="product.features && product.features.length > 0" class="bg-card rounded-lg border border-border p-4">
            <h4 class="font-semibold mb-3">Caractéristiques</h4>
            <ul class="space-y-2">
              <li v-for="(feature, index) in product.features" :key="index" class="flex items-center text-sm">
                <Check class="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                {{ feature }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Reviews Section -->
      <div class="mt-16" >
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-2xl font-bold">Avis clients ({{ product.reviews_count || 0 }})</h2>
          <button 
            v-if="isAuthenticated"
            @click="openReviewModal" 
            class="bg-primary hover:bg-primary-hover text-primary-foreground h-10 px-4 rounded-md font-medium"
          >
            Écrire un avis
          </button>
          <button 
            v-else
            @click="goToLogin"
            class="bg-primary hover:bg-primary-hover text-primary-foreground h-10 px-4 rounded-md font-medium"
          >
            Connectez-vous pour noter
          </button>
        </div>

        <div  v-if="product.reviews && product.reviews.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="review in product.reviews" :key="review.id" class="bg-card rounded-lg border border-border p-4">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center space-x-1">
                <Star
                  v-for="i in 5"
                  :key="i"
                  :class="[
                    'h-4 w-4',
                    i <= review.rating
                      ? 'fill-secondary text-secondary'
                      : 'text-muted-foreground/30'
                  ]"
                />
              </div>
              <span v-if="review.verified" class="bg-secondary/20 text-secondary px-2 py-1 rounded-full text-xs">
                Achat vérifié
              </span>
            </div>
            <p class="text-sm text-muted-foreground mb-2">
              {{ review.comment }}
            </p>
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{{ review.user?.name || 'Anonyme' }}</span>
              <span>{{ formatDate(review.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Similar Products -->
      <div class="mt-16" v-if="product.similar_products && product.similar_products.length > 0">
        <h2 class="text-2xl font-bold mb-8">Produits similaires</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <router-link 
            v-for="similarProduct in product.similar_products" 
            :key="similarProduct.id" 
            :to="`/product/${similarProduct.slug.trim()}`"
          >
            <div class="group hover:shadow-lg transition-all duration-300 overflow-hidden bg-card rounded-lg border border-border">
              <div class="aspect-square overflow-hidden bg-muted">
                <img 
                  :src="getProductImageUrl(similarProduct.image || similarProduct.cover_image)"
                  :alt="similarProduct.name"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  @error="handleImageError"
                />
              </div>
              <div class="p-3">
                <h3 class="font-medium text-sm line-clamp-2 group-hover:text-secondary transition-colors">
                  {{ similarProduct.name }}
                </h3>
                <div class="flex items-center gap-1 mt-1">
                  <Star class="h-3 w-3 fill-secondary text-secondary" />
                  <span class="text-xs text-muted-foreground">{{ similarProduct.rating || '0.0' }}</span>
                </div>
                <div class="flex items-center gap-2 mt-1">
                  <p class="font-bold">{{ formatPrice(similarProduct.promo_price || similarProduct.price) }}</p>
                  <p v-if="similarProduct.promo_price" class="text-xs text-muted-foreground line-through">
                    {{ formatPrice(similarProduct.price) }}
                  </p>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Review Modal -->
    <div v-if="showReviewModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card rounded-lg border border-border">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold">Écrire un avis</h3>
            <button
              @click="closeReviewModal"
              class="p-2 hover:bg-accent rounded-full transition-colors"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <form @submit.prevent="handleSubmitReview" class="space-y-4">
            <div>
              <label class="block text-sm font-medium mb-2">
                Votre nom
              </label>
              <input
                type="text"
                v-model="newReview.author"
                placeholder="Entrez votre nom"
                required
                class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label class="block text-sm font-medium mb-2">
                Note
              </label>
              <div class="flex space-x-1">
                <button
                  v-for="star in 5"
                  :key="star"
                  type="button"
                  @click="setReviewRating(star)"
                  class="p-1"
                >
                  <Star
                    :class="[
                      'h-6 w-6',
                      star <= newReview.rating
                        ? 'fill-secondary text-secondary'
                        : 'text-muted-foreground/30'
                    ]"
                  />
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium mb-2">
                Votre avis
              </label>
              <textarea
                v-model="newReview.comment"
                placeholder="Partagez votre expérience avec ce produit..."
                rows="4"
                required
                class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              ></textarea>
            </div>

            <div class="flex space-x-3">
              <button 
                type="button" 
                @click="closeReviewModal"
                class="flex-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 rounded-md font-medium transition-normal"
              >
                Annuler
              </button>
              <button type="submit" class="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground h-10 px-4 rounded-md font-medium transition-normal">
                Publier
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  Star, 
  Heart, 
  ShoppingCart, 
  Minus, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Check,
  X,
  Truck,
  Shield,
  RefreshCw,
  Package
} from 'lucide-vue-next';
 import { mapState, mapActions } from 'vuex'
// import { generateProductSeo, generateProductSchema } from '@/plugins/seo'
export default {
  
  name: 'ProductDetail',
  components: {
    Star, 
    Heart, 
    ShoppingCart, 
    Minus, 
    Plus, 
    ChevronLeft, 
    ChevronRight,
    Check,
    X,
    Truck,
    Shield,
    RefreshCw,
    Package
  },
  data() {
    return {
      loading: false,
      error: null,
      product: null,
      selectedSize: null,
      selectedColor: null,
      selectedVariant: null,
      quantity: 1,
      currentImageIndex: 0,
      showReviewModal: false,
      newReview: {
        rating: 5,
        comment: '',
        author: ''
      },
      defaultProductImage: require('@/assets/default-product.png'),
    };
  },
  computed: {
    slug() {
      console
      return this.$route.params.slug;
    },
    
    isAuthenticated() {
      return this.$store.state.token !== null;
    },
    
    isInWishlist() {
      if (!this.product || !this.$store.state.wishlists) return false;
      return this.$store.state.wishlists.some(item => item.product_id === this.product.id);
    },
    
    // Product images array
    productImages() {
      const images = [];
      
      // Add cover image
      if (this.product?.cover_image) {
        images.push(this.getProductImageUrl(this.product.cover_image));
      }
      
      // Add additional images
      if (this.product?.additional_images?.length > 0) {
        this.product.additional_images.forEach(img => {
          images.push(this.getProductImageUrl(img.image_path));
        });
      }
      
      // Add default image if no images
      if (images.length === 0) {
        images.push(this.defaultProductImage);
      }
      
      return images;
    },
    
    currentImage() {
      return this.productImages[this.currentImageIndex] || this.defaultProductImage;
    },
    
    hasMultipleImages() {
      return this.productImages.length > 1;
    },
    
    hasDiscount() {
      if (!this.product) return false;
      const price = parseFloat(this.product.price) || 0;
      const promoPrice = parseFloat(this.product.promo_price) || 0;
      return promoPrice > 0 && promoPrice < price;
    },
    
    discountPercentage() {
      if (!this.hasDiscount || !this.product) return 0;
      const price = parseFloat(this.product.price);
      const promoPrice = parseFloat(this.product.promo_price);
      return Math.round(((price - promoPrice) / price) * 100);
    },
    
    finalPrice() {
      if (!this.product) return 0;
      const promoPrice = parseFloat(this.product.promo_price);
      const price = parseFloat(this.product.price);
      return promoPrice > 0 ? promoPrice : price;
    },
    
    hasSizes() {
      return this.product?.sizes && Array.isArray(this.product.sizes) && this.product.sizes.length > 0;
    },
    
    hasColors() {
      return this.product?.colors && Array.isArray(this.product.colors) && this.product.colors.length > 0;
    },
    
    hasSpecifications() {
      return this.product?.specifications && Object.keys(this.product.specifications).length > 0;
    },
    
    categorySlug() {
      return this.product?.category?.slug || this.product?.category?.name?.toLowerCase().replace(/\s+/g, '-') || 'category';
    }
  },
  methods: {
    //  updateSeoForProduct() {
    //   if (!this.product.id) return
      
    //   const productSeo = generateProductSeo(this.product)
    //   productSeo.schema = generateProductSchema(this.product)
      
    //   // Émettre l'événement pour mettre à jour le SEO
    //   this.$emit('updateSeo', productSeo)
    // },
    async fetchProduct() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$store.dispatch('fetchProduct', this.slug);
        this.product = response;
      } catch (error) {
        console.error('Error fetching product:', error);
        this.error = error.response?.data?.message || 'Produit non trouvé';
      } finally {
        this.loading = false;
      }
    },
    
    getProductImageUrl(imagePath) {
      if (!imagePath) return this.defaultProductImage;
      
      // Si c'est déjà une URL complète
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      
      // Si c'est un chemin relatif
      return `${this.$store.state.baseUrl}${imagePath}`;
    },
    
    handleImageError(event) {
      event.target.src = this.defaultProductImage;
    },
    
    formatPrice(price) {
      const priceNumber = parseFloat(price) || 0;
      return new Intl.NumberFormat('fr-FR').format(priceNumber) + ' FCFA';
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    },
    
    nextImage() {
      if (this.hasMultipleImages) {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.productImages.length;
      }
    },
    
    prevImage() {
      if (this.hasMultipleImages) {
        this.currentImageIndex = (this.currentImageIndex - 1 + this.productImages.length) % this.productImages.length;
      }
    },
    
    setCurrentImageIndex(index) {
      this.currentImageIndex = index;
    },
    
    selectSize(size) {
      this.selectedSize = size;
    },
    
    selectColor(color) {
      this.selectedColor = color;
    },
    
    toggleWishlist() {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (this.isInWishlist) {
        this.$store.dispatch('removeFromWishlist', this.product.id);
      } else {
        this.$store.dispatch('addToWishlist', this.product);
      }
    },
    
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    
    increaseQuantity() {
      if (this.product && this.quantity < this.product.stock_quantity) {
        this.quantity++;
      }
    },
    
    async addToCart() {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (!this.product.in_stock || this.product.stock_quantity <= 0) {
        alert('Ce produit n\'est pas disponible');
        return;
      }
      
      try {
        const cartData = {
          id: this.product.id,
          quantity: this.quantity,
          size: this.selectedSize,
          color: this.selectedColor,
          price: this.finalPrice
        };
        
        await this.$store.dispatch('addToCart', cartData);
        
        // Afficher une notification
        this.$toast?.success?.('Produit ajouté au panier') || alert('Produit ajouté au panier');
      } catch (error) {
        console.error('Error adding to cart:', error);
        const errorMessage = error.response?.data?.error || 'Erreur lors de l\'ajout au panier';
        this.$toast?.error?.(errorMessage) || alert(errorMessage);
      }
    },
    
    goToLogin() {
      this.$router.push('/auth');
    },
    
    openReviewModal() {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      this.showReviewModal = true;
    },
    
    closeReviewModal() {
      this.showReviewModal = false;
      this.resetNewReview();
    },
    
    setReviewRating(rating) {
      this.newReview.rating = rating;
    },
    
    async handleSubmitReview() {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      try {
        // Ici, vous devrez créer une action dans votre store pour envoyer la review
        // Pour l'instant, on simule l'envoi
        console.log('Submitting review:', this.newReview);
        
        this.showReviewModal = false;
        this.resetNewReview();
        
        this.$toast?.success?.('Merci pour votre avis !') || alert('Merci pour votre avis !');
      } catch (error) {
        console.error('Error submitting review:', error);
        this.$toast?.error?.('Erreur lors de l\'envoi de l\'avis') || alert('Erreur lors de l\'envoi de l\'avis');
      }
    },
    
    resetNewReview() {
      this.newReview = {
        rating: 5,
        comment: '',
        author: ''
      };
    }
  },
  // async created() {
  //   // Charger le produit si pas déjà dans le store
  //   if (!this.product.id) {
  //     await this.fetchProduct(this.slug)
  //   }
    
    // Mettre à jour le SEO
   
  // },
  // watch: {
  //   slug() {
  //     this.fetchProduct();
  //     //  if(this.product && this.product.id ){
  //         this.updateSeoForProduct()
  //       // }
        
  //   },
  //     immediate: true
    
    
   
  // },
 async mounted() {
    this.fetchProduct();
    //  this.updateSeoForProduct()
  }
};
</script>

<style scoped>
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.transition-normal {
  transition: all 0.2s ease-in-out;
}

/* Animation pour le spinner */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
